import { darken, lighten } from 'polished';
import styled, { css } from 'styled-components';
import COLORS from '../../../constants/COLORS';
import mixins from '../../../styles/mixins';

interface CalendarDayProps {
  isDisabled: boolean;
  isToday: boolean;
  isSelected: boolean;
  isPrev: boolean;
  isNext: boolean;
  isLines: boolean;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 14px 34px rgba(34, 35, 46, 0.25);
  border-radius: ${({ theme }) => (theme.isMobile ? '16px 16px 0px 0px' : '16px')};
  width: ${({ theme }) => (theme.isMobile ? '100%' : '360px')};
  height: ${({ theme }) => theme.isMobile && '100%'};
  margin: 0;
  padding: 24px 20px;
  color: #000;
  overflow-y: auto;
  box-sizing: border-box;
  user-select: none;
`;

export const Header = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 8px;
`;

export const Title = styled.div`
  margin: 0 0 12px;
  font-weight: 500;
  font-size: 24px;
  line-height: 34px;
  color: #141414;
`;

export const HeaderCell = styled.div<{ isWeekend?: boolean }>`
  width: 32px;
  height: 16px;
  padding-bottom: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ isWeekend }) => (isWeekend ? 'rgba(255, 59, 48, 0.6)' : 'rgba(0, 0, 0, 0.64)')};
  text-align: center;
  flex-grow: 1;
`;

export const Body = styled.div`
  width: 100%;
  margin: 0 0 16px;
`;

export const Row = styled.div<any>`
  display: ${(props) => (props.isHidden ? 'none' : 'flex')};
`;

export const Cell = styled.div<any>`
  display: flex;
  justify-content: center;
  padding: 0;
  flex-grow: 1;
  margin-top: 2px;
`;

export const CalendarDay = styled.button<CalendarDayProps>`
  position: relative;
  user-select: none;
  display: flex;
  transition: background-color 0.4s, transform 0.4s, box-shadow 0.4s;
  align-items: center;
  justify-content: center;
  font-size: small;
  line-height: 15px;
  margin: 0px;
  border: none;
  position: relative;
  padding: 4px 4px;
  width: 44px;
  height: 44px;
  color: #000;
  border-radius: 8px;
  background: transparent;
  border: 1px solid transparent;
  box-sizing: border-box;
  outline: none;
  cursor: pointer;

  @media screen and (max-width: 360px) {
    width: 40px;
    height: 40px;
  }

  ${(props) =>
    props.isDisabled &&
    css`
      cursor: default;
      opacity: 0.2;
    `}

  ${(props) =>
    !props.isDisabled &&
    !props.isSelected &&
    css`
      border: 1px solid ${({ theme: { buttonBackground } }) => buttonBackground || '#42d483'};

      &:hover {
        background: #f5f5f5;
        z-index: 100;
      }
    `}

    ${(props) =>
    !props.isDisabled &&
    props.isToday &&
    css`
      border: 1px dotted gray;
    `}

    ${(props) =>
    !props.isDisabled &&
    props.isSelected &&
    css`
      color: ${({ theme: { buttonColor } }) => buttonColor || '#fff'};
      background: ${({ theme: { buttonBackground } }) => buttonBackground || '#42d483'};
      z-index: 100;

      circle {
        fill: ${({ theme: { buttonBackground } }) =>
          buttonBackground === '#42D483' ? '#fff' : '#42D483'};
      }

      path {
        fill: ${({ theme: { buttonBackground } }) =>
          buttonBackground !== '#42D483' ? '#fff' : '#42D483'};
      }
    `}


      ${({ isLines }) => {
    if (isLines) {
      return css`
        position: relative;

        ${TicketsStatus} {
          position: absolute;
          height: 2px;
          width: calc(100% - 24px);
          top: 8px;
          left: 0;
          right: 0;
          margin: 0 auto;
          border: 0;
          border-radius: 2px;
        }
      `;
    }
  }}
`;

export const TimesContainer = styled.div`
  margin: 0 0 12px;
`;

export const TimesTitle = styled.div`
  margin: 0 0 4px;
  font-weight: 500;
  font-size: 24px;
  line-height: 34px;
  color: #141414;
`;

export const TimesItems = styled.div`
  display: flex;
  overflow-y: hidden;
  margin-right: -20px;
  ${mixins.hideScrollBar}
`;

export const TimeItem = styled.div`
  box-sizing: border-box;
  padding: 2px;
  flex-shrink: 0;
`;

export const Time = styled.button<{ isSelected: boolean; isLines: boolean }>`
  position: relative;
  width: 100%;
  max-height: 38px;
  padding: 8px 12px;
  border: 1px solid
    ${({ isSelected, theme }) => (isSelected ? theme.buttonBackground || COLORS.BLUE : '#f0f2f5')};
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${({ isSelected, theme }) => (isSelected ? theme.buttonColor || '#fff' : '#151035')};
  text-align: center;
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.buttonBackground || COLORS.BLUE : 'transparent'};
  transition: 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: ${({ isSelected }) => !isSelected && '#f0f2f5'};
  }

  ${({ isLines }) => {
    if (isLines) {
      return css`
        position: relative;
        padding: 8px 12px 8px 20px;

        ${TicketsStatus} {
          position: absolute;
          height: calc(100% - 16px);
          width: 2px;
          top: 8px;
          left: 11px;
          border: 0;
          border-radius: 2px;
        }
      `;
    }
  }}
`;

export const Arrow = styled.div<{ isOpened: boolean }>`
  position: absolute;
  width: 8px;
  height: 5px;
  left: 0;
  right: 0;
  bottom: 4px;
  margin: auto;
  opacity: 0.4;
  line-height: 0;
  transform: rotate(180deg);
  transition: all 0.2s linear;

  ${(props) =>
    props.isOpened &&
    css`
      transform: rotate(0);
      opacity: 1;

      svg {
        fill: #2f80ed;
      }
    `}
`;

export const Wrapper = styled.div<{ top: number; right: number }>`
  z-index: 9994;

  ${({ theme, top, right }) =>
    theme.isMobile
      ? css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        `
      : css`
          position: absolute;
          top: ${top}px;
          right: ${right}px;
          padding-top: 12px;
        `}
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9993;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 34px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  cursor: pointer;

  svg {
    fill: #000;
  }
`;

export const SelectContainer = styled.div`
  position: relative;
  flex-grow: 1;
  border-radius: 3px;
  margin: 0 2px 24px;
`;

export const SelectValueContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  padding: 0 16px;
  border: none;
  background-color: #f0f2f5;
  transition: 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: ${lighten(0.02, '#f0f2f5')};
  }

  &:focus {
    background-color: ${darken(0.1, '#f0f2f5')};
  }

  svg path {
    stroke: #000;
  }
`;

export const SelectValue = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  color: #151035;
`;

export const SelectOptions = styled.ul`
  position: absolute;
  z-index: 101;
  top: 100%;
  left: 0;
  width: 100%;
  padding: 0;
  margin: 0;
  max-height: 200px;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  overflow-y: auto;
  list-style: none;
  box-shadow: 0 20px 10px rgba(0, 0, 0, 0.1);
`;

export const SelectOptionItem = styled.li``;

export const SelectOption = styled.button<{ active?: boolean }>`
  width: 100%;
  padding: 8px 16px;
  margin: 0;
  text-align: inherit;
  border: none;
  color: ${({ active }) => !active && 'gray'};
  background-color: #f0f2f5;
  cursor: pointer;
`;

export const Submit = styled.button`
  display: block;
  width: 100%;
  padding: 13px 14px;
  margin-top: auto;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: ${({ theme: { buttonColor } }) => buttonColor || '#fff'};
  background: ${({ theme: { buttonBackground } }) => buttonBackground || '#42d483'};
  transition: 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => lighten(0.1, theme.buttonBackground || '#42d483')};
  }

  &:focus {
    background: ${({ theme }) => darken(0.1, theme.buttonBackground || '#42d483')};
  }
`;

const TicketsStatus = styled.span`
  position: absolute;
  top: -6px;
  right: -12px;
  width: 12px;
  height: 12px;
  margin-right: 8px;
  display: inline-block;
  border: 2px solid ${COLORS.WHITE};
  border-radius: 100%;
`;

export const HasTicketsDate = styled(TicketsStatus)`
  background-color: ${COLORS.GREEN};
`;

export const NoTicketsDate = styled(TicketsStatus)`
  background-color: ${COLORS.GREY_D9};
`;

export const TimeHasTickets = styled(TicketsStatus)`
  position: static;
  margin-right: 6px;
  background-color: ${COLORS.GREEN};
`;
export const TimeNoTickets = styled(TicketsStatus)`
  position: static;
  margin-right: 6px;
  background-color: ${COLORS.GREY_D9};
`;
